import React, { useEffect, useState, useRef, MouseEventHandler } from "react"
import styles from "./StockPricesBox.module.css"
import TrendArrow from "@components/general/icons/trendArrow/TrendArrow"
import throttle from "lodash/throttle"
import { isToday } from "@utils/time"

const StockPriceBoxArrowButton = ({
  onClick,
  direction,
}: {
  onClick: MouseEventHandler<HTMLButtonElement>
  direction: string
}) => {
  return (
    <div className={direction === "left" ? styles.leftGradient : styles.rightGradient}>
      <button className={styles.arrowButton} onClick={onClick}>
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          style={{ transform: direction === "left" ? "rotate(180deg)" : "rotate(0deg)" }}
        >
          <g fill="none" fillRule="evenodd">
            <path className={styles.arrowButtonSvgBackground} d="M0 0h24v24H0z" />
            <path
              className={styles.arrowButtonSvgArrow}
              d="m18.629 12.058-5.621 6.558-1.302-1.116 3.932-4.586-10.138.001v-1.714l10.138-.001-3.932-4.584L13.008 5.5l5.62 6.558z"
              fillRule="nonzero"
            />
          </g>
        </svg>
      </button>
    </div>
  )
}

type StockPriceBoxItemType = {
  currentPriceDate: string
  currentPriceDateFormattedSecs: string
  currentPriceDateSoloFormatted: string
  currentPriceFormatted: string
  dailyChangeRelFormatted: string
  fuwUrl: string
  name: string
}

const StockPriceBoxItem = ({ item }: { item: StockPriceBoxItemType }) => (
  <li className={styles.item}>
    <a href={`https://wp.fuw.ch${item.fuwUrl}`} target="_parent">
      <div className={styles.itemTitle}>{item.name}</div>
      <div className={styles.itemValueAbs}>{item.currentPriceFormatted}</div>

      <div className={styles.itemValueRelContainer}>
        <TrendArrow positive={item.dailyChangeRelFormatted[0] === "+"} />
        <div
          className={`${styles.itemValueRel} ${
            item.dailyChangeRelFormatted[0] === "+" ? styles.positive : styles.negative
          }`}
        >
          {item.dailyChangeRelFormatted}
        </div>
      </div>
      <div className={styles.itemDate}>
        {isToday(new Date(item.currentPriceDate))
          ? item.currentPriceDateFormattedSecs.split(" ")[0].split(":").slice(0, 2).join(":")
          : item.currentPriceDateSoloFormatted}
      </div>
    </a>
  </li>
)

const StockPricesBox = (props: any) => {
  const [scrollPosition, setScrollPosition] = useState(0)
  const [width, setWidth] = useState(0)
  const scrollWindow = useRef<HTMLUListElement>(null)

  const handleScrolling = () => {
    if (scrollWindow.current !== null) {
      setScrollPosition(scrollWindow.current.scrollLeft)
      setWidth(scrollWindow.current.scrollWidth - scrollWindow.current.offsetWidth)
    }
  }

  const handleLeftButtonClick = () => {
    if (scrollWindow.current !== null) {
      scrollWindow.current.scrollBy({
        top: 0,
        left: -100,
        behavior: "smooth",
      })
    }
  }

  const handleRightButtonClick = () => {
    if (scrollWindow.current !== null) {
      scrollWindow.current.scrollBy({
        top: 0,
        left: 100,
        behavior: "smooth",
      })
    }
  }

  useEffect(() => {
    handleScrolling()
    window.addEventListener("resize", throttle(handleScrolling, 100))
  }, [])

  return (
    <div className={styles.container}>
      {scrollPosition > 0 && <StockPriceBoxArrowButton onClick={handleLeftButtonClick} direction="left" />}
      {scrollPosition < width && <StockPriceBoxArrowButton onClick={handleRightButtonClick} direction="right" />}

      <ul className={styles.box} onScroll={throttle(handleScrolling, 100)} ref={scrollWindow}>
        {props.data.Values.map((item: any, i: any) => (
          <StockPriceBoxItem item={item} key={i} />
        ))}
      </ul>
    </div>
  )
}

export default StockPricesBox
